import { useMemo } from "react";
import { z } from "zod";
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/http";
import type { TableParamsApi } from "@/lib/table-params";
import { zSpringPage, zTimestamp } from "./_schemas";

export const pipelineSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  status: z.string(),
  executionMode: z.string().nullish(),
  sourceConnectorId: z.string().nullish(),
  workspaceId: z.string().nullish(),
  archivedAt: zTimestamp.nullish(),
  destConnectorId: z.string().nullish(),
  createdAt: zTimestamp.nullish(),
  updatedAt: zTimestamp.nullish(),
});
export type PipelineSummary = z.infer<typeof pipelineSummarySchema>;

export const stepSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  stepType: z.string(),
  stepOrder: z.number(),
  enabled: z.boolean().nullish(),
  onError: z.string().nullish(),
  retryCount: z.number().nullish(),
  retryDelayMs: z.number().nullish(),
  timeoutMs: z.number().nullish(),
  config: z.record(z.string(), z.unknown()).nullish(),
});
export type PipelineStep = z.infer<typeof stepSchema>;

export const pipelineDetailSchema = pipelineSummarySchema.extend({
  config: z.record(z.string(), z.unknown()).nullish(),
  steps: z.array(stepSchema).nullish(),
});
export type PipelineDetail = z.infer<typeof pipelineDetailSchema>;

export const pipelineExecutionSchema = z.object({
  id: z.string(),
  pipelineId: z.string().nullish(),
  status: z.string(),
  triggerType: z.string().nullish(),
  triggeredBy: z.string().nullish(),
  startedAt: zTimestamp.nullish(),
  finishedAt: zTimestamp.nullish(),
  durationMs: z.number().nullish(),
  recordsRead: z.number().nullish(),
  recordsWritten: z.number().nullish(),
  recordsFailed: z.number().nullish(),
  currentStep: z.string().nullish(),
  errorMessage: z.string().nullish(),
  context: z.record(z.string(), z.unknown()).nullish(),
});
export type PipelineExecution = z.infer<typeof pipelineExecutionSchema>;

const stepExecSchema = z.object({
  id: z.string().nullish(),
  stepId: z.string().nullish(),
  stepName: z.string().nullish(),
  status: z.string().nullish(),
  startedAt: zTimestamp.nullish(),
  finishedAt: zTimestamp.nullish(),
  durationMs: z.number().nullish(),
  recordsIn: z.number().nullish(),
  recordsOut: z.number().nullish(),
  errorMessage: z.string().nullish(),
});
export const executionDetailSchema = pipelineExecutionSchema.extend({
  steps: z.array(stepExecSchema).nullish(),
});
export type ExecutionDetail = z.infer<typeof executionDetailSchema>;

// ── Queries ─────────────────────────────────────────────────────────────────

export function pipelinesQuery(params: { status?: string; size?: number } = {}) {
  const search = new URLSearchParams({ size: String(params.size ?? 200) });
  if (params.status) search.set("status", params.status);
  return queryOptions({
    queryKey: ["pipelines", "list", params.status ?? null, params.size ?? 200],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines?${search}`).then((r) =>
        zSpringPage(pipelineSummarySchema).parse(r),
      ),
  });
}

export function usePipelines(params?: { status?: string; size?: number }) {
  return useQuery(pipelinesQuery(params));
}

/** Server-paginated + filtered pipelines for the Pipelines screen. */
export function usePipelinesPage(params: TableParamsApi) {
  const search = params.toSearchParams().toString();
  return useQuery({
    queryKey: ["pipelines", "page", search],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines?${search}`).then((r) =>
        zSpringPage(pipelineSummarySchema).parse(r),
      ),
    placeholderData: keepPreviousData,
  });
}

export function usePipelineNames(): Record<string, string> {
  const { data } = usePipelines();
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of data?.content ?? []) map[p.id] = p.name;
    return map;
  }, [data]);
}

export function pipelineQuery(id: string) {
  return queryOptions({
    queryKey: ["pipelines", "detail", id],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines/${id}`).then((r) => pipelineDetailSchema.parse(r)),
  });
}
export function usePipeline(id: string) {
  return useQuery(pipelineQuery(id));
}

export function usePipelineExecutions(id: string, params: { page?: number; size?: number } = {}) {
  const { page = 0, size = 25 } = params;
  return useQuery({
    queryKey: ["pipelines", "executions", id, page, size],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines/${id}/executions?page=${page}&size=${size}`).then((r) =>
        zSpringPage(pipelineExecutionSchema).parse(r),
      ),
  });
}

export function executionDetailQuery(pipelineId: string, execId: string) {
  return queryOptions({
    queryKey: ["pipelines", "execution-detail", pipelineId, execId],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines/${pipelineId}/executions/${execId}`).then((r) =>
        executionDetailSchema.parse(r),
      ),
  });
}
export function useExecutionDetail(pipelineId: string, execId: string) {
  return useQuery(executionDetailQuery(pipelineId, execId));
}

const versionSchema = z
  .object({
    id: z.string(),
    versionNumber: z.number().nullish(),
    label: z.string().nullish(),
    createdBy: z.string().nullish(),
    createdAt: zTimestamp.nullish(),
  })
  .passthrough();

export function usePipelineVersions(id: string) {
  return useQuery({
    queryKey: ["pipelines", "versions", id],
    queryFn: () =>
      apiRequest<unknown>(`/pipelines/${id}/versions`).then((r) => z.array(versionSchema).parse(r)),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export interface CreatePipelineInput {
  name: string;
  description?: string;
  executionMode?: string;
  sourceConnectorId?: string;
  destConnectorId?: string;
}

export function usePipelineMutations(id?: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pipelines"] });
    if (id) qc.invalidateQueries({ queryKey: ["pipelines", "detail", id] });
  };

  return {
    create: useMutation({
      mutationFn: (body: CreatePipelineInput) =>
        apiRequest<PipelineDetail>("/pipelines", { method: "POST", body }).then((r) =>
          pipelineDetailSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (body: Partial<PipelineDetail> & { steps?: PipelineStep[] }) =>
        apiRequest<PipelineDetail>(`/pipelines/${id}`, { method: "PUT", body }),
      onSuccess: invalidate,
    }),
    updateSteps: useMutation({
      mutationFn: (steps: PipelineStep[]) =>
        apiRequest<PipelineDetail>(`/pipelines/${id}/steps`, { method: "PUT", body: steps }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (pid: string) => apiRequest<void>(`/pipelines/${pid}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    run: useMutation({
      mutationFn: (pid: string) =>
        apiRequest<{ executionId: string }>(`/pipelines/${pid}/run`, {
          method: "POST",
          body: { mode: "async" },
        }),
      onSuccess: invalidate,
    }),
    setActive: useMutation({
      mutationFn: ({ pid, active }: { pid: string; active: boolean }) =>
        apiRequest<{ status: string }>(`/pipelines/${pid}/${active ? "activate" : "pause"}`, {
          method: "PATCH",
        }),
      onSuccess: invalidate,
    }),
    moveWorkspace: useMutation({
      mutationFn: ({ pid, workspaceId }: { pid: string; workspaceId: string | null }) =>
        apiRequest<PipelineDetail>(`/pipelines/${pid}/workspace`, {
          method: "PATCH",
          body: { workspaceId },
        }),
      onSuccess: invalidate,
    }),
    duplicate: useMutation({
      mutationFn: (pid: string) =>
        apiRequest<PipelineDetail>(`/pipelines/${pid}/duplicate`, { method: "POST" }).then((r) =>
          pipelineDetailSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    setArchived: useMutation({
      mutationFn: ({ pid, archived }: { pid: string; archived: boolean }) =>
        apiRequest<void>(`/pipelines/${pid}/${archived ? "archive" : "unarchive"}`, {
          method: "PATCH",
        }),
      onSuccess: invalidate,
    }),
    replay: useMutation({
      mutationFn: ({ pid, execId }: { pid: string; execId: string }) =>
        apiRequest<{ executionId: string }>(`/pipelines/${pid}/executions/${execId}/replay`, {
          method: "POST",
        }),
      onSuccess: invalidate,
    }),
    backfill: useMutation({
      mutationFn: ({ pid, body }: { pid: string; body: Record<string, unknown> }) =>
        apiRequest<unknown>(`/pipelines/${pid}/backfill`, { method: "POST", body }),
      onSuccess: invalidate,
    }),
  };
}

export interface BulkResult {
  ok: number;
  failed: { id: string; error: string }[];
}
export interface BulkInput {
  ids: string[];
  op: "archive" | "unarchive" | "delete" | "move" | "activate" | "pause";
  workspaceId?: string | null;
}

export function useBulkPipelines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkInput) =>
      apiRequest<BulkResult>("/pipelines/bulk", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines"] }),
  });
}
